import { useState } from 'react';
import { Button, Group, Modal, Stack, TextInput } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import { ErrorAlert } from '@/components/error-alert';
import { api } from '@/lib/api';
import { getErrorMessage } from '@/lib/error-message';

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function CreateOrganizationModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const changeName = (value: string) => {
    setName(value);

    if (!slugTouched) setSlug(slugify(value));
  };

  const submit = async () => {
    setError(null);
    setIsSubmitting(true);

    const { data, error: apiError } = await api.organizations.post({ name: name.trim(), slug: slug.trim() });

    setIsSubmitting(false);

    if (apiError || !data) {
      setError(getErrorMessage(apiError, 'Failed to create organization'));

      return;
    }

    onClose();
    await navigate({ to: '/system/organizations/$id', params: { id: data.id } });
  };

  return (
    <Modal opened onClose={onClose} title="Create Organization">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <Stack gap="md">
          <ErrorAlert message={error} />
          <TextInput
            label="Name"
            placeholder="Acme Corp"
            value={name}
            onChange={(e) => changeName(e.currentTarget.value)}
            data-autofocus
          />
          <TextInput
            label="Slug"
            description="Unique identifier. Auto-generated from name."
            placeholder="acme-corp"
            value={slug}
            onChange={(e) => {
              setSlug(e.currentTarget.value);
              setSlugTouched(true);
            }}
            styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)' } }}
          />
          <Group justify="flex-end" mt="xs">
            <Button variant="subtle" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={isSubmitting} disabled={!name.trim() || !slug.trim()}>
              Create
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
